import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

/**
 * Light-first, dark via a `.dark` class on <html>. Because every colour token is
 * a CSS variable, flipping that one class re-themes the entire application —
 * no component knows the theme exists.
 */
const ThemeContext = createContext(null);
const STORAGE_KEY = 'distrxpay_theme';

const readStored = () => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() =>
    document.documentElement.classList.contains('dark') ? 'dark' : 'light',
  );

  useEffect(() => {
    const isDark = theme === 'dark';
    document.documentElement.classList.toggle('dark', isDark);
    document
      .querySelector('meta[name=theme-color]')
      ?.setAttribute('content', isDark ? '#18181b' : '#ffffff');
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* a theme preference is not worth failing over */
    }
  }, [theme]);

  // Follow the OS until the user expresses a preference of their own.
  useEffect(() => {
    if (readStored()) return undefined;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => setTheme(e.matches ? 'dark' : 'light');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const toggle = useCallback(() => setTheme((t) => (t === 'dark' ? 'light' : 'dark')), []);
  const value = useMemo(() => ({ theme, isDark: theme === 'dark', toggle, setTheme }), [theme, toggle]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside a ThemeProvider');
  return context;
}
